import { useEffect, useState } from 'react'
import { Pressable, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'

import { Icon, symbolName } from '../../../components/ui/Icon'
import { VocaliseLogo } from '../../../components/VocaliseLogo'
import { APP_TOP_BAR_ROW_HEIGHT } from '../../../constants/app-top-bar-layout'
import { colors } from '../../../theme'
import { useDashboardDailyTip } from '../hooks/useDashboardDailyTip'
import { useDashboardTipVisibility } from '../hooks/useDashboardTipVisibility'

const COLLAPSE_SURFACE_DELAY_MS = 240

export function DashboardTopBar({
  isOnLightBackground,
}: {
  isOnLightBackground: boolean
}) {
  const insets = useSafeAreaInsets()

  const {
    showTip: isTipExpanded,
    handleDismissTip,
    handleShowTip,
  } = useDashboardTipVisibility()

  const { t } = useTranslation(['dashboard'])
  const { showTipToggle } = useDashboardDailyTip()

  const [showCollapsedSurface, setShowCollapsedSurface] = useState(true)

  useEffect(() => {
    if (isTipExpanded || showCollapsedSurface) {
      return
    }

    const timeoutId = setTimeout(() => {
      setShowCollapsedSurface(true)
    }, COLLAPSE_SURFACE_DELAY_MS)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isTipExpanded, showCollapsedSurface])

  const handleTipTogglePress = () => {
    setShowCollapsedSurface(false)
    if (isTipExpanded) {
      handleDismissTip()
    } else {
      handleShowTip()
    }
  }

  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        backgroundColor:
          isOnLightBackground || (!isTipExpanded && showCollapsedSurface)
            ? colors.backgroundPrimary
            : 'transparent',
        height: APP_TOP_BAR_ROW_HEIGHT + insets.top,
      }}
      className="flex justify-end px-4"
    >
      <View
        className="flex-row items-center justify-between"
        style={{ height: APP_TOP_BAR_ROW_HEIGHT }}
      >
        <VocaliseLogo
          height={14}
          color={
            isOnLightBackground || (!isTipExpanded && showCollapsedSurface)
              ? colors.ink
              : 'white'
          }
        />
        {showTipToggle ? (
          <Pressable
            accessibilityLabel={
              isTipExpanded
                ? t('dashboard:collapse_daily_tip')
                : t('dashboard:show_daily_tip')
            }
            accessibilityRole="button"
            className="items-center justify-center rounded-full"
            hitSlop={8}
            onPress={handleTipTogglePress}
            style={{
              width: 32,
              height: 32,
            }}
          >
            <Icon
              name={
                isTipExpanded
                  ? symbolName('xmark', 'close')
                  : symbolName('lightbulb.max', 'emoji_objects')
              }
              size={18}
              color={
                isOnLightBackground || (!isTipExpanded && showCollapsedSurface)
                  ? colors.ink
                  : 'white'
              }
            />
          </Pressable>
        ) : null}
      </View>
    </View>
  )
}
